import { SITE_URL, SITE_NAME } from "./PageMeta";

export function buildWebsiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: SITE_NAME,
    url: SITE_URL,
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${SITE_URL}/browse?search={search_term_string}`,
      },
      "query-input": "required name=search_term_string",
    },
  };
}

export function buildOrganizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: SITE_NAME,
    url: SITE_URL,
    logo: "https://cdn.builder.io/api/v1/image/assets%2F2651dca913d34eafa320b193e1b2c34e%2Fcc449eaa12f04ec8a633e5d5dc5c0623",
    contactPoint: {
      "@type": "ContactPoint",
      email: "medspadirectorypro@gmail.com",
      contactType: "customer service",
    },
  };
}

export function buildWebPageSchema(opts: {
  url: string;
  name: string;
  description?: string;
  datePublished?: string;
  dateModified?: string;
  inLanguage?: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${opts.url}#webpage`,
    url: opts.url,
    name: opts.name,
    ...(opts.description && { description: opts.description }),
    inLanguage: opts.inLanguage ?? "en-US",
    datePublished: opts.datePublished ?? "2026-01-01",
    dateModified: opts.dateModified ?? new Date().toISOString().split("T")[0],
  };
}

export function buildSpeakableSchema(opts: {
  url: string;
  cssSelectors?: string[];
}) {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    url: opts.url,
    speakable: {
      "@type": "SpeakableSpecification",
      cssSelector: opts.cssSelectors ?? [
        "h1",
        "h2",
        "[data-speakable]",
        ".faq-answer",
      ],
    },
  };
}

export function buildBreadcrumbSchema(
  items: { name: string; url: string }[]
) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

export function buildLocalBusinessSchema(listing: {
  name: string;
  slug: string;
  description?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  phone?: string | null;
  website?: string | null;
  email?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  rating?: number | null;
  review_count?: number | null;
  hours?: { day: string; open: string; close: string }[] | null;
  logo_url?: string | null;
  image_url?: string | null;
  social_links?: { facebook?: string; instagram?: string; tiktok?: string; youtube?: string } | null;
  tags?: string[] | null;
}) {
  const schema: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "HealthAndBeautyBusiness",
    name: listing.name,
    url: `${SITE_URL}/spa/${listing.slug}`,
  };

  if (listing.description) schema.description = listing.description;

  if (listing.address || listing.city || listing.state) {
    schema.address = {
      "@type": "PostalAddress",
      ...(listing.address && { streetAddress: listing.address }),
      ...(listing.city && { addressLocality: listing.city }),
      ...(listing.state && { addressRegion: listing.state }),
      ...(listing.zip && { postalCode: listing.zip }),
      addressCountry: "US",
    };
  }

  if (listing.phone) schema.telephone = listing.phone;
  if (listing.website) schema.url = listing.website;
  if (listing.email) schema.email = listing.email;

  if (listing.latitude && listing.longitude) {
    schema.geo = {
      "@type": "GeoCoordinates",
      latitude: listing.latitude,
      longitude: listing.longitude,
    };
    schema.hasMap = `https://www.google.com/maps/search/?api=1&query=${listing.latitude},${listing.longitude}`;
  } else if (listing.address || listing.city) {
    const q = encodeURIComponent(
      [listing.name, listing.address, listing.city, listing.state]
        .filter(Boolean)
        .join(", "),
    );
    schema.hasMap = `https://www.google.com/maps/search/?api=1&query=${q}`;
  }

  if (listing.rating && listing.rating > 0) {
    schema.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue: listing.rating,
      reviewCount: Math.max(listing.review_count ?? 1, 1),
      bestRating: 5,
      worstRating: 1,
    };
  }

  // Generic price range hint — directional only; Google understands "$$".
  schema.priceRange = "$$";
  schema.currenciesAccepted = "USD";
  schema.paymentAccepted = "Cash, Credit Card";

  if (listing.city || listing.state) {
    schema.areaServed = {
      "@type": "City",
      name: listing.city ?? listing.state,
      ...(listing.state && {
        containedInPlace: {
          "@type": "State",
          name: listing.state,
        },
      }),
    };
  }

  if (listing.logo_url) schema.logo = listing.logo_url;
  if (listing.image_url) schema.image = listing.image_url;

  if (listing.hours && Array.isArray(listing.hours)) {
    const dayMap: Record<string, string> = {
      monday: "Monday",
      tuesday: "Tuesday",
      wednesday: "Wednesday",
      thursday: "Thursday",
      friday: "Friday",
      saturday: "Saturday",
      sunday: "Sunday",
    };
    const specs = listing.hours
      .filter((h) => h.open && h.open.toLowerCase() !== "closed")
      .map((h) => ({
        "@type": "OpeningHoursSpecification",
        dayOfWeek: dayMap[h.day.toLowerCase()] || h.day,
        opens: h.open,
        closes: h.close,
      }));
    if (specs.length) schema.openingHoursSpecification = specs;
  }

  if (listing.social_links && typeof listing.social_links === "object") {
    const links = Object.values(listing.social_links).filter(Boolean);
    if (links.length) schema.sameAs = links;
  }

  // availableService — each treatment tag exposed as a MedicalProcedure
  if (listing.tags && Array.isArray(listing.tags) && listing.tags.length > 0) {
    const tags = listing.tags.slice(0, 30);
    schema.availableService = tags.map((t) => ({
      "@type": "MedicalProcedure",
      name: t,
      procedureType: "https://schema.org/TherapeuticProcedure",
    }));
    // Also expose as an OfferCatalog → Google maps this to "Services offered"
    schema.hasOfferCatalog = {
      "@type": "OfferCatalog",
      name: `Services offered by ${listing.name}`,
      itemListElement: tags.map((t) => ({
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: t,
          serviceType: "MedSpa Treatment",
          provider: {
            "@type": "HealthAndBeautyBusiness",
            name: listing.name,
          },
        },
      })),
    };
  }

  return schema;
}

export function buildArticleSchema(article: {
  title: string;
  description: string;
  slug: string;
  author: string;
  publishDate: string;
  updatedDate?: string;
  image: string;
  tags?: string[];
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.description,
    url: `${SITE_URL}/blog/${article.slug}`,
    image: article.image,
    datePublished: article.publishDate,
    dateModified: article.updatedDate || article.publishDate,
    author: {
      "@type": "Organization",
      name: article.author,
      url: SITE_URL,
    },
    publisher: {
      "@type": "Organization",
      name: SITE_NAME,
      url: SITE_URL,
      logo: {
        "@type": "ImageObject",
        url: "https://cdn.builder.io/api/v1/image/assets%2F2651dca913d34eafa320b193e1b2c34e%2Fcc449eaa12f04ec8a633e5d5dc5c0623",
      },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `${SITE_URL}/blog/${article.slug}`,
    },
    ...(article.tags && article.tags.length > 0 && { keywords: article.tags.join(", ") }),
  };
}

export function buildFAQSchema(
  faqs: { question: string; answer: string }[]
) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };
}

/**
 * Schema.org MedicalProcedure markup for treatment landing pages.
 * Helps Google understand the page is about a specific medical/aesthetic
 * procedure and unlock richer SERP appearance.
 */
export function buildMedicalProcedureSchema(opts: {
  name: string;
  description: string;
  url: string;
  imageUrl?: string;
  bodyLocation?: string;
  preparationText?: string;
  followupText?: string;
  cityName?: string;
  priceRangeLow?: number;
  priceRangeHigh?: number;
  priceCurrency?: string;
  priceUnit?: string;
}) {
  const schema: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "MedicalProcedure",
    name: opts.name,
    description: opts.description,
    url: opts.url,
    procedureType: "https://schema.org/TherapeuticProcedure",
  };
  if (opts.imageUrl) schema.image = opts.imageUrl;
  if (opts.bodyLocation) schema.bodyLocation = opts.bodyLocation;
  if (opts.preparationText) schema.preparation = opts.preparationText;
  if (opts.followupText) schema.followup = opts.followupText;
  if (opts.cityName) {
    schema.availableService = {
      "@type": "MedicalProcedure",
      name: `${opts.name} in ${opts.cityName}`,
    };
  }
  if (opts.priceRangeLow && opts.priceRangeHigh) {
    schema.offers = {
      "@type": "AggregateOffer",
      priceCurrency: opts.priceCurrency ?? "USD",
      lowPrice: opts.priceRangeLow,
      highPrice: opts.priceRangeHigh,
      ...(opts.priceUnit && { description: opts.priceUnit }),
    };
  }
  return schema;
}
